import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X, ChevronRight, Utensils, Phone } from "lucide-react";
import { RESTAURANT } from "@/data/restaurant";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/story", label: "Our Story" },
  { to: "/menu", label: "The Menu" },
  { href: "https://order.toasttab.com/egiftcards/infuse-modern-indian-bistro-4-5-waddington-road", label: "Gift Cards", external: true },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        transparent ? "py-5 bg-transparent" : "nav-glass py-3"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-baseline gap-2 group shrink-0">
          <span className={`font-display text-3xl md:text-4xl tracking-tight transition-colors duration-500 ${transparent ? "text-white" : "text-[var(--infuse-emerald)]"}`}>
            Infuse
          </span>
          <span className={`font-eyebrow hidden sm:inline transition-colors duration-500 ${transparent ? "text-[var(--infuse-gold-soft)]" : "text-[var(--infuse-gold)]"}`}>
            · St Albans
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            n.external ? (
              <a
                key={n.label}
                href={n.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`font-eyebrow text-[0.7rem] transition-colors duration-300 hover:text-[var(--infuse-pomegranate)] ${
                  transparent ? "text-white/85" : "text-[var(--infuse-ink)]/70"
                }`}
              >
                {n.label}
              </a>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-eyebrow text-[0.7rem] transition-colors duration-300 hover:text-[var(--infuse-pomegranate)] ${
                    isActive
                      ? (transparent ? "text-[var(--infuse-gold-soft)]" : "text-[var(--infuse-emerald)]")
                      : (transparent ? "text-white/85" : "text-[var(--infuse-ink)]/70")
                  }`
                }
              >
                {n.label}
              </NavLink>
            )
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            href={RESTAURANT.orderUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="order-now-nav-cta"
            className={`hidden md:inline-flex items-center gap-1.5 font-eyebrow text-[0.7rem] px-4 py-3 border transition-all duration-300 ${
              transparent
                ? "border-[var(--infuse-gold-soft)] text-[var(--infuse-gold-soft)] hover:bg-[var(--infuse-gold-soft)] hover:text-[var(--infuse-emerald-dark)]"
                : "border-[var(--infuse-emerald)] text-[var(--infuse-emerald)] hover:bg-[var(--infuse-emerald)] hover:text-white"
            }`}
          >
            <Utensils size={13} strokeWidth={1.6} />
            Order Now
          </a>
          <a
            href={RESTAURANT.bookUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="book-table-cta"
            className="btn-primary hidden md:inline-flex"
          >
            Book a Table
            <ChevronRight size={14} strokeWidth={1.5} />
          </a>
          <a
            href={RESTAURANT.phoneHref}
            data-testid="call-now-cta"
            className={`hidden md:inline-flex items-center gap-1.5 font-eyebrow text-[0.7rem] px-4 py-3 border transition-all duration-300 ${
              transparent
                ? "border-white/70 text-white hover:bg-white hover:text-[var(--infuse-emerald-dark)]"
                : "border-[var(--infuse-ink)]/40 text-[var(--infuse-ink)] hover:bg-[var(--infuse-ink)] hover:text-white"
            }`}
          >
            <Phone size={13} strokeWidth={1.6} />
            Call Now
          </a>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
            className={`lg:hidden p-2 transition-colors ${transparent ? "text-white" : "text-[var(--infuse-emerald)]"}`}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <MenuIcon size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden nav-glass border-t border-amber-500/20 mt-3">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              n.external ? (
                <a
                  key={n.label}
                  href={n.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-display text-2xl text-[var(--infuse-emerald)]"
                >
                  {n.label}
                </a>
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-display text-2xl text-[var(--infuse-emerald)]"
                >
                  {n.label}
                </NavLink>
              )
            ))}
            <a
              href={RESTAURANT.bookUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-book-table-cta"
              className="btn-primary mt-3 w-full"
            >
              Book a Table <ChevronRight size={14} />
            </a>
            <a
              href={RESTAURANT.orderUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-order-now-cta"
              className="btn-ghost-gold w-full"
            >
              Order Now <Utensils size={14} />
            </a>
            <a
              href={RESTAURANT.phoneHref}
              data-testid="mobile-call-now-cta"
              className="inline-flex items-center justify-center gap-2 w-full border border-[var(--infuse-ink)]/40 text-[var(--infuse-ink)] font-eyebrow text-[0.78rem] uppercase tracking-[0.22em] py-3 rounded-sm hover:bg-[var(--infuse-ink)] hover:text-white transition-colors"
            >
              <Phone size={14} strokeWidth={1.6} />
              Call Now
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
